// EnhancedSpeedometer.jsx
import React, { useEffect, useRef } from "react";
import { motion, useAnimation } from "framer-motion";
import Lottie from "lottie-react";
import { Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";

import particlesGreen from "assets/lottie/Star Animation.json";
import particlesOrange from "assets/lottie/Star Animation.json";
import particlesRed from "assets/lottie/Star Animation.json";
import iconChart from "assets/lottie/Growth_Chart.json";
import iconSafe from "assets/lottie/Shield_icon.json";
import iconWarning from "assets/lottie/WarningAlert.json";
import iconFire from "assets/lottie/dangerALert.json";

import "./EnhancedSpeedometer.scss";

ChartJS.register(ArcElement, Tooltip, Legend);

const EnhancedSpeedometer = ({ score = 0 }: any) => {
  const needleRef = useRef(null);
  const controls = useAnimation();

  const level = score < 40 ? "low" : score < 70 ? "medium" : "high";
  const levelClass = `${level}-risk`;

  // Needle rotation
  const rotation = (score / 100) * 180 - 90;

  useEffect(() => {
    controls.start({
      rotate: rotation,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
        duration: 1.5
      }
    });
  }, [rotation, controls]);

  const getColor = () => {
    if (level === "low") return "#10B981";
    if (level === "medium") return "#F59E0B";
    return "#EF4444";
  };

  const getGradientColor = () => {
    if (level === "low") return "#10B981";
    if (level === "medium") return "#F59E0B";
    return "#EF4444";
  };

  const data = {
    datasets: [
      {
        data: [score, 100 - score],
        backgroundColor: [getGradientColor(), "#F3F4F6"],
        borderWidth: 0,
        cutout: "85%",
        circumference: 180,
        rotation: 270,
        borderRadius: 10,
        spacing: 2,
      },
    ],
  };

  const options = {
    plugins: { legend: { display: false }, tooltip: { enabled: false } },
    animation: false,
  };

  const getIcon = () => {
    if (level === "low") return iconSafe;
    if (level === "medium") return iconWarning;
    return iconFire;
  };

  const getRiskText = () => {
    if (level === "low") return "Risque faible";
    if (level === "medium") return "Risque modéré";
    return "Risque élevé";
  };

  const getRiskDescription = () => {
    if (level === "low") return "";
    if (level === "medium") return "Des mesures supplémentaires sont recommandées";
    return "Une action immédiate est nécessaire";
  };

  const getActiveSegments = () => {
    if (score < 20) return [0];
    if (score < 40) return [0, 1];
    if (score < 60) return [0, 1, 2];
    if (score < 80) return [0, 1, 2, 3];
    return [0, 1, 2, 3, 4];
  };

  const activeSegments = getActiveSegments();

  return (
    <div className={"EnhancedSpeedometer"}>
      <motion.div
        className={`speedometer-card ${levelClass}`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <div className="speedometer-header">
          <div className="lottie-icon">
            <Lottie animationData={iconChart} loop={true} style={{ width: 48, height: 48 }} />
          </div>
          <h3>Score ID Protect</h3>
        </div>


        <div className="speedometer-body">
          <div className="chart-container-2">
            {/*<div className="scale-markings">
            {[...Array(11)].map((_, i) => (
              <div
                key={i}
                className="scale-tick"
                style={{
                  transform: `rotate(${i * 18}deg)`,
                  transformOrigin: '50% 100%'
                }}
              >
                {i % 2 === 0 && (
                  <span className="scale-label">
                    {i * 10}
                  </span>
                )}
              </div>
            ))}
          </div>
*/}
            <div className="doughnut-container">
              <Doughnut data={data} options={options} />
            </div>

            <motion.div
              ref={needleRef}
              className="needle"
              animate={controls}
              initial={{ rotate: -90 }}
            />

            <div className="needle-base" />

            <div className="score-display">
              <motion.p
                className="score-value"
                key={score}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ type: "spring", stiffness: 200, delay: 0.5 }}
              >
                {score}
              </motion.p>
              <p className="score-label">SCORE</p>
            </div>
          </div>
        </div>

        <div className="speedometer-footer">
          <div className="risk-indicator">
            <div className="risk-icon">
              <Lottie animationData={getIcon()} loop={true} style={{ width: 72, height: 72 }} />
            </div>
            <div className="risk-text">
              <h4 className="risk-level">{getRiskText()}</h4>
              <p className="risk-description">{getRiskDescription()}</p>
            </div>
          </div>

          <div className="risk-meter">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className={`risk-segment segment-${i + 1} ${activeSegments.includes(i) ? 'active' : ''}`}
              />
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default EnhancedSpeedometer;